import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { DocDialog } from '../common/DocDialog';

interface BulkImportDocDialogProps {
    open: boolean;
    handleClose: () => void;
    // Required, not defaulted: the guide must not describe a route the API answers with a 404.
    inviteEnabled: boolean;
}

const CSV_TEMPLATE = `Username,Email,First Name,Last Name,Phone Number
jsmith,jsmith@example.com,John,Smith,+1234567890
jdoe,jdoe@example.com,Jane,Doe,`;

const BulkImportDocDialog: React.FC<BulkImportDocDialogProps> = ({ open, handleClose, inviteEnabled }) => {

    const handleDownloadTemplate = () => {
        const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bulk_users_template.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <DocDialog open={open} title="Bulk User Import Guide" handleClose={handleClose}>
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button variant="outlined" size="small" onClick={handleDownloadTemplate}>
                    Download CSV Template
                </Button>
            </Box>
            <Typography variant="h6" gutterBottom>Overview</Typography>
            <Typography variant="body2" paragraph>
                The Bulk User Import feature allows you to create multiple user accounts at once
                by uploading a CSV file. The import wizard guides you through seven steps: uploading
                and mapping CSV data, reviewing users, selecting applications, roles, and
                organizations, and finally submitting the import and reviewing the results.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 1: Prepare a CSV File</Typography>
            <Typography variant="body2" paragraph>
                Create a CSV file with the following columns. The first row must be the header row.
            </Typography>
            <Box component="table" sx={(theme) => ({ width: '100%', mb: 2, '& td, & th': { border: '1px solid', borderColor: 'divider', p: 1, fontSize: theme.typography.body2.fontSize } })}>
                <thead>
                    <tr>
                        <th>Field</th>
                        <th>Required</th>
                        <th>Description</th>
                    </tr>
                </thead>
                <tbody>
                    <tr><td>Username</td><td>Yes</td><td>Unique login identifier (max 128 characters)</td></tr>
                    <tr><td>Email</td><td>Yes</td><td>Valid email address, must be unique (max 256 characters)</td></tr>
                    <tr><td>First Name</td><td>Yes</td><td>User's first name (max 64 characters)</td></tr>
                    <tr><td>Last Name</td><td>Yes</td><td>User's last name (max 64 characters)</td></tr>
                    <tr><td>Phone Number</td><td>No</td><td>Phone number in international format (e.g., +1234567890)</td></tr>
                </tbody>
            </Box>
            <Typography variant="body2" paragraph>
                Column headers are matched flexibly. For example, "EmailAddress", "email", or "Mail"
                all map to the Email field. Similarly, "FirstName", "First", or "GivenName" map to
                First Name.
            </Typography>

            {inviteEnabled && <>
                <Typography variant="h6" gutterBottom>Inviting users instead of importing them</Typography>
                <Typography variant="body2" paragraph>
                    <strong>Invite Users</strong> takes a CSV with a single required column, <strong>Email</strong>.
                    Any other columns are ignored: the invited user chooses a password and completes their own
                    name and contact details from the invitation link. Applications, roles and organizations
                    chosen in the dialog apply to every invited user. An address that already belongs to a
                    user, or that already holds an invitation, fails validation — use <strong>Resend</strong> on
                    the users list for an existing invitee.
                </Typography>
            </>}

            <Typography variant="h6" gutterBottom>Step 2: Upload & Map Fields</Typography>
            <Typography variant="body2" paragraph>
                Click "Import Users" on the Users page and select your CSV file. The system
                parses the file and automatically maps columns to system fields based on header names.
                You can adjust the mappings manually if needed. All required fields must be mapped
                before proceeding.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 3: Review</Typography>
            <Typography variant="body2" paragraph>
                Review the parsed data in a table view. Rows with validation errors are highlighted
                in red — hover over the error indicator to see details. You can remove problematic
                rows using the delete button. All errors must be resolved before proceeding.
            </Typography>
            <Typography variant="body2" paragraph>
                This step also includes a <strong>Send Welcome Email</strong> checkbox. When enabled,
                each successfully created user will receive a welcome email containing their username
                and a temporary password.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 4: Applications</Typography>
            <Typography variant="body2" paragraph>
                Select which applications to enable for all imported users. Toggle the switch next
                to each application to enable or disable it. Hover over the info icon to see the
                application description. This step is optional — you can skip it if no applications
                need to be enabled at import time.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 5: Roles</Typography>
            <Typography variant="body2" paragraph>
                Select roles to assign to all imported users. Toggle the switch next to each
                role to assign or unassign it — hover over the info icon to see the role
                description. Use <strong>Quick Search</strong> to narrow a long role list by
                name or description; the caption above the list shows how many roles are
                selected. These are global roles applied to every imported user;
                organization-specific roles are assigned in the next step. This step is
                optional.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 6: Organizations</Typography>
            <Typography variant="body2" paragraph>
                Assign organization memberships and roles for all imported users. You can add
                multiple organizations, each with its own set of role assignments. Tenant
                administrators can select any organization; other administrators are automatically
                assigned to their own organization and can only configure roles within it.
            </Typography>

            <Typography variant="h6" gutterBottom>Step 7: Import & Results</Typography>
            <Typography variant="body2" paragraph>
                The system re-validates all user data on the server side. The import is
                all-or-nothing: if any row fails validation, no users are created and the
                failed rows are listed with per-row error details. You have two ways to
                recover:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    Remove each failed row using its <strong>Remove from import</strong> button.
                    Once all failed rows are removed, the <strong>Retry Import</strong> button
                    becomes enabled and re-submits the remaining users.
                </Typography>
                <Typography component="li" variant="body2">
                    Use <strong>Back</strong> to return to the earlier steps, adjust the data
                    or selections, and submit again.
                </Typography>
            </Box>
            <Typography variant="body2" paragraph>
                When validation passes, users are queued for asynchronous creation — a progress
                indicator tracks the operation and the users appear in the users list shortly.
                Click <strong>Done</strong> to close the wizard.
            </Typography>

            <Typography variant="h6" gutterBottom>Important Notes</Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    <strong>Caution when editing the CSV in Excel.</strong> Excel auto-detects
                    the <strong>Phone Number</strong> column as numeric and silently strips the
                    leading <code>+</code> on save, which makes the value fail validation. To
                    avoid this, edit the file in a plain-text editor (Notepad, VS Code), or in
                    Excel use <em>Data → From Text/CSV</em> and explicitly set the Phone Number
                    column type to <em>Text</em> before saving.
                </Typography>
                <Typography component="li" variant="body2">
                    Duplicate usernames or email addresses — both within the file and against
                    existing users — will be flagged as errors.
                </Typography>
                <Typography component="li" variant="body2">
                    Rows cannot be edited inside the wizard. To correct a value, fix the source
                    CSV file and re-upload it (this resets the mapping and review state), or
                    remove the affected rows.
                </Typography>
                <Typography component="li" variant="body2">
                    Roles selected on the <strong>Roles</strong> step apply globally to all
                    imported users, while roles on the <strong>Organizations</strong> step are
                    scoped to the selected organization.
                </Typography>
                <Typography component="li" variant="body2">
                    All imported users are created with <strong>Enabled</strong> status
                    and <strong>Force Password Reset</strong> on first login.
                </Typography>
                <Typography component="li" variant="body2">
                    Passwords are auto-generated by the system. If "Send Welcome Email" is
                    enabled, credentials are emailed to each user automatically.
                </Typography>
                <Typography component="li" variant="body2">
                    User creation is processed asynchronously. For large imports, it may take
                    a few moments for all users to appear in the list.
                </Typography>
                <Typography component="li" variant="body2">
                    Additional user details such as addresses and contacts can be configured
                    on each user's detail page after import.
                </Typography>
            </Box>
        </DocDialog>
    );
};

export default BulkImportDocDialog;
